import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  AutoBroadcastSettings,
  Feature,
  SellerAccountClient,
} from 'src/WebApiController';

export const useAutoBroadcastSellerAccountSettings = () => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();
  const hasFeature = useUserHasFeature(Feature.AutoBroadcastWhenPricing);

  const queryKey = [
    'SellerAccountClient.getAutoBroadcastSettings',
    activeAccountWebClientConfig.activeAccountId,
    hasFeature,
  ];

  const shouldQuery =
    hasFeature && activeAccountWebClientConfig.activeAccountId != null;

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!shouldQuery) return null;
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).getAutoBroadcastSettings();
    },
    enabled: shouldQuery,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: async (settings: AutoBroadcastSettings) => {
      return new SellerAccountClient(
        activeAccountWebClientConfig
      ).mergeAutoBroadcastSettings(settings);
    },
    onError: (err: ErrorTypes, settings) => {
      showErrorDialog('SellerAccountClient.mergeAutoBroadcastSettings', err, {
        trackErrorData: { settings },
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetched: query.isFetched,
    mutation,
  };
};
